import * as React from 'react';
import { useImperativeHandle, forwardRef } from 'react';
import { ComboBox, Item, Key } from '@adobe/react-spectrum';
import { useLanguageDataLoader } from '../../data-components/Contexts/DataLoader';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { AtollonTextField } from '../../../ui/TextField/AtollonTextField';
import Add from '@spectrum-icons/workflow/Add';
import styles from './NamesList.module.css';

export interface NameInfo {
  name: string;
  languageId: string | null;
}

export interface INamesListProps {
  onChange?: (items: NameInfo[]) => void;
  defaultValue?: NameInfo[];
  onValidationChange?: (isValid: boolean) => void;
}

export interface INamesListRef {
  getValue: () => NameInfo[];
  isValid: () => boolean;
}

export const NamesList = forwardRef<INamesListRef, INamesListProps>((props, forwardedRef) => {
  const { onChange, defaultValue, onValidationChange } = props;
  const { store } = useDependency();
  
  // Initialize with one empty item or defaultValue
  const [items, setItems] = React.useState<NameInfo[]>(() => {
    if (defaultValue && defaultValue.length > 0) {
      return defaultValue;
    }
    return [{ name: '', languageId: null }];
  });

  // Load language data
  const { data: languages, error: languageError, loading: languageLoading } = useLanguageDataLoader();

  // Set default language from user preferences when languages are loaded
  React.useEffect(() => {
    if (!languageLoading && languages.length > 0) {
      const userLanguageId = store?.session?.userPreferences?.language;
      if (userLanguageId) {
        // Find the language matching the user's preference
        const userLanguage = languages.find(
          (lang) => String(lang.id) === String(userLanguageId)
        );
        if (userLanguage && userLanguage.isoShort) {
          // Update items if the first item has no language set
          setItems((currentItems) => {
            if (currentItems.length > 0 && currentItems[0].languageId === null) {
              const updatedItems = [...currentItems];
              updatedItems[0] = {
                ...updatedItems[0],
                languageId: userLanguage.isoShort,
              };
              return updatedItems;
            }
            return currentItems;
          });
        }
      }
    }
  }, [languageLoading, languages, store?.session?.userPreferences?.language]);

  // Validation: Check if names are empty and languages are distinct
  const validateItems = React.useCallback((itemsToValidate: NameInfo[]): boolean => {
    // Check if any name is empty or whitespace only
    const hasEmptyName = itemsToValidate.some(item => !item.name || item.name.trim() === '');
    if (hasEmptyName) {
      return false;
    }

    // Check if all selected languages are distinct (excluding null values)
    const selectedLanguages = itemsToValidate
      .map(item => item.languageId)
      .filter((langId): langId is string => langId !== null);
    const uniqueLanguages = new Set(selectedLanguages);
    
    // If there are selected languages, they must all be distinct
    if (selectedLanguages.length > 0 && selectedLanguages.length !== uniqueLanguages.size) {
      return false;
    }

    return true;
  }, []);

  // Validate items whenever they change
  const isValid = React.useMemo(() => validateItems(items), [items, validateItems]);

  // Expose getValue and isValid methods via ref
  useImperativeHandle(forwardedRef, () => ({
    getValue: () => items,
    isValid: () => isValid,
  }));

  // Notify parent of changes
  React.useEffect(() => {
    onChange?.(items);
  }, [items, onChange]);

  // Notify parent of validation changes
  React.useEffect(() => {
    onValidationChange?.(isValid);
  }, [isValid, onValidationChange]);

  // Handle name change for a specific item
  const handleNameChange = (index: number, value: string) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], name: value };
    setItems(updatedItems);
  };

  // Handle language selection change for a specific item
  const handleLanguageChange = (index: number, key: Key | null) => {
    const updatedItems = [...items];
    const newLanguageId = key ? key.toString() : null;
    
    // Check if the selected language is already used by another item
    if (newLanguageId !== null) {
      const isDuplicate = updatedItems.some(
        (item, i) => i !== index && item.languageId === newLanguageId
      );
      
      if (isDuplicate) {
        // Don't allow duplicate language selection
        return;
      }
    }
    
    updatedItems[index] = { 
      ...updatedItems[index], 
      languageId: newLanguageId 
    };
    setItems(updatedItems);
  };

  // Add a new item
  const handleAddLanguage = () => {
    setItems([...items, { name: '', languageId: null }]);
  };

  // Remove an item
  const handleRemoveItem = (index: number) => {
    if (items.length > 1) {
      const updatedItems = items.filter((_, i) => i !== index);
      setItems(updatedItems);
    }
  };

  if (languageError) {
    return <div className={styles.error}>Error loading languages: {languageError}</div>;
  }

  return (
    <div className={styles.namesList}>
      {items.map((item, index) => (
        <div key={index} className={styles.itemRow}>
          <div className={styles.inputGroup}>
            <div className={styles.nameInput}>
              <AtollonTextField
                label="Name of the view"
                value={item.name}
                onInput={(e) => handleNameChange(index, e.currentTarget.value)}
                placeholder=""
                width="100%"
                labelPosition="top"
              />
            </div>
            <div className={styles.languageInput}>
              <ComboBox
                label="Language"
                selectedKey={item.languageId}
                onSelectionChange={(key) => handleLanguageChange(index, key)}
                description="Select a language"
                width="100%"
                isDisabled={languageLoading}
              >
                {languages.map((lang) => (
                  <Item key={lang.isoShort} textValue={lang.languageLong || ''}>
                    {lang.languageLong}
                  </Item>
                ))}
              </ComboBox>
            </div>
          </div>
          {items.length > 1 && (
            <button
              type="button"
              className={styles.removeButton}
              onClick={() => handleRemoveItem(index)}
              aria-label="Remove language"
            >
              ×
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        className={styles.addButton}
        onClick={handleAddLanguage}
        aria-label="Add language"
      >
        <Add size="S" />
        <span>Add language</span>
      </button>
    </div>
  );
});

NamesList.displayName = 'NamesList';
